import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { LocalStorageConstants, OrganisationTypeConstants } from "../../../../constants";
import { PortfolioDatesData, PortfolioMilestone, PortfolioPhaseDates, SelectData } from "../../../../models";
import {
  searchPipelineDeals,
  SearchPipelineDealsRequest,
  searchWorkflowTaskMilestones,
} from "../../../../service/query";
import { FilterGroups, ServiceError, Status } from "../../../../service/Shared";
import { useAuth } from "../../../../useAuth";
import { jsonReviver, safeLocalStorage } from "../../../../utils";
import { getDealListingRoute } from "../../../../utils/routes";
import { dataGridMapFilterCriteria, GanttRoadmapRow, Toast } from "../../../../widget";
import { toGanttDealRows } from "./utils";

interface UsePortfolioRoadmapPageReturnData {
  errors: ServiceError[] | undefined;
  ganttRows: GanttRoadmapRow[];
  onRowClick: (rowId: string) => void;
  dealsMultiSelectData: SelectData | undefined;
  roadmapDealUuids: string[];
  setRoadmapDealUuids: Dispatch<SetStateAction<string[]>>;
  showAddDealsModal: boolean;
  setShowAddDealsModal: Dispatch<SetStateAction<boolean>>;
  closeSelectDealsModal: () => void;
  isLoading: boolean;
  hasData: boolean;
  onSaveDealsSelectData: () => void;
  fetchSelectData: (name?: string) => Promise<void>;
}

const MAX_DEALS = 15;
/**
 * One read covers every selected deal. A selection capped at `MAX_DEALS` cannot plausibly reach this
 * ceiling, and the read reports it rather than truncating in silence if it ever does.
 */
const MAX_MILESTONES = 1000;

export const usePortfolioRoadmapPage = (): UsePortfolioRoadmapPageReturnData => {
  const { user, currentOrganisationUuid } = useAuth();
  const navigate = useNavigate();

  const portfolioRoadmapLocalStorageItemName = `${user?.userUuid ? `${user.userUuid}_` : ""}${LocalStorageConstants.PIPELINE_ROADMAP_PREFERENCES}`;

  const [isLoading, setIsLoading] = useState(true);
  const [errors, setErrors] = useState<ServiceError[]>();
  const [data, setData] = useState<PortfolioDatesData[]>([]);
  const [dealsMultiSelectData, setDealsMultiSelectData] = useState<SelectData>();
  const [roadmapDealUuids, setRoadmapDealUuids] = useState<string[]>([]);
  const [showAddDealsModal, setShowAddDealsModal] = useState(false);

  const hasData = data.length > 0;

  const ganttRows = useMemo(() => toGanttDealRows(data), [data]);

  // The row label used to be an anchor to the deal listing; clicking a row (name or bar) still lands there.
  const onRowClick = useCallback(
    (rowId: string): void => {
      const route = getDealListingRoute(rowId, OrganisationTypeConstants.ASSET_MANAGER);
      if (!route) return;

      navigate(route);
    },
    [navigate]
  );

  const updatePortfolioRoadmapLocalStorage = useCallback(
    (uuids: string[]) => {
      try {
        safeLocalStorage.setItem(portfolioRoadmapLocalStorageItemName, JSON.stringify(uuids));
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (err) {
        const message = err instanceof Error ? err.message : "An unknown error occurred while saving to local storage.";
        Toast.warn({ message });
      }
    },
    [portfolioRoadmapLocalStorageItemName]
  );

  const baseDateFilterCriteria = useCallback(() => {
    const filterCriteria = dataGridMapFilterCriteria([]);
    return filterCriteria;
  }, []);

  const fetchSelectData = useCallback(
    async (name?: string) => {
      if (!currentOrganisationUuid) return;

      const filterCriteria = baseDateFilterCriteria();

      if (name) {
        filterCriteria.name = {
          operator: "startsWith",
          value: name,
        };
      }

      const res = await searchPipelineDeals({
        organisationUuid: currentOrganisationUuid,
        paging: {
          limit: 50,
          beforeCursor: null,
          afterCursor: null,
        },
        filter: { results: filterCriteria },
      });

      setDealsMultiSelectData(
        res.data?.results
          .filter((deal) => deal.dealDates !== null && deal.dealDates !== "{}")
          .map((deal) => ({
            key: deal.uuid,
            value: deal.name,
          })) ?? []
      );
    },
    [baseDateFilterCriteria, currentOrganisationUuid]
  );

  /**
   * The selected deals' milestone tasks, grouped by the deal each belongs to.
   *
   * Filtered on `parentObjectUuid` — the uniform parent object, which is set for a general task as
   * well as for one hanging off a workflow, so a single read covers both of a deal's task routes.
   * `objectUuid` is deliberately not used: on a workflow task it names the sub-object the task is
   * about rather than the deal. Undated tasks are left server-side, since the chart places a diamond
   * by date and would drop them anyway.
   *
   * A failed read costs the diamonds, not the roadmap: the stage bars stand on their own, so the
   * milestones are reported missing and the rows are still drawn.
   *
   * Ordered by deadline so that the ceiling, if it is ever reached, keeps the near end of the
   * timeline rather than an arbitrary slice of it.
   */
  const fetchMilestones = useCallback(async (uuids: string[]): Promise<Map<string, PortfolioMilestone[]>> => {
    const res = await searchWorkflowTaskMilestones({
      paging: {
        limit: MAX_MILESTONES,
        beforeCursor: null,
        afterCursor: null,
      },
      sort: [{ key: "results.deadlineDate", direction: "asc" }],
      filter: {
        results: {
          deadlineDate: { operator: "neq", value: null },
          isMilestone: { operator: "eq", value: true },
          parentObjectUuid: { operator: "in", value: uuids },
        },
      },
    }).catch(() => undefined);

    if (!res || res.status !== Status.Success || !res.data) {
      Toast.warn({ message: "Unable to load the milestones for this roadmap, so none are shown." });
      return new Map();
    }

    /*
      Only the first page is read, so a selection past the ceiling has to say so: a diamond that is
      missing without a word reads as "this deal has no milestones", which is the very thing the
      roadmap is drawn to show.
    */
    if (res.data.results.length < res.data.paging.total) {
      Toast.warn({ message: "Some milestones are not shown on this roadmap." });
    }

    return res.data.results.reduce<Map<string, PortfolioMilestone[]>>((byDeal, task) => {
      // A task with no parent cannot be attributed, so it is dropped rather than defaulted onto a deal.
      if (!task.parentObjectUuid) return byDeal;

      const milestone: PortfolioMilestone = { uuid: task.uuid, label: task.name, date: task.deadlineDate };

      return byDeal.set(task.parentObjectUuid, [...(byDeal.get(task.parentObjectUuid) ?? []), milestone]);
    }, new Map());
  }, []);

  const fetchRoadmapData = useCallback(
    async (uuids: string[]) => {
      if (!currentOrganisationUuid || uuids.length === 0) {
        setData([]);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);

      const filterCriteria = baseDateFilterCriteria();
      filterCriteria.uuid = {
        operator: "in",
        value: uuids,
      };

      const request: SearchPipelineDealsRequest & FilterGroups<SearchPipelineDealsRequest["filter"]> = {
        organisationUuid: currentOrganisationUuid,
        paging: {
          limit: MAX_DEALS,
          beforeCursor: null,
          afterCursor: null,
        },
        filter: { results: filterCriteria },
      };

      // Both reads are scoped by the same uuids, so neither waits on the other.
      const [res, milestonesByDeal] = await Promise.all([searchPipelineDeals(request), fetchMilestones(uuids)]);

      const roadmapData: PortfolioDatesData[] =
        res.data?.results.map((deal) => {
          const dates = JSON.parse(deal.dealDates ?? "{}", jsonReviver) as Record<string, PortfolioPhaseDates>;

          return {
            uuid: deal.uuid,
            displayName: deal.name,
            relatedProjects: deal.relatedObjects.map((relatedObject) => relatedObject.name),
            dates,
            milestones: milestonesByDeal.get(deal.uuid) ?? [],
          };
        }) ?? [];

      setData(roadmapData);

      const availableDealUuids = new Set(roadmapData.map((deal) => deal.uuid));
      const filteredUuids = uuids.filter((uuid) => availableDealUuids.has(uuid));

      if (filteredUuids.length !== uuids.length) {
        setRoadmapDealUuids(filteredUuids);
        updatePortfolioRoadmapLocalStorage(filteredUuids);
      }

      setIsLoading(false);
    },
    [baseDateFilterCriteria, currentOrganisationUuid, fetchMilestones, updatePortfolioRoadmapLocalStorage]
  );

  const fetchAndSetRoadmapDealUuids = useCallback(() => {
    const storedDeals = safeLocalStorage.getItem(portfolioRoadmapLocalStorageItemName);
    if (storedDeals) {
      const uuids = JSON.parse(storedDeals) as string[];
      setRoadmapDealUuids(uuids);
      fetchRoadmapData(uuids);
    } else {
      setIsLoading(false);
    }
  }, [fetchRoadmapData, portfolioRoadmapLocalStorageItemName]);

  const closeSelectDealsModal = useCallback(() => {
    setShowAddDealsModal(false);
    fetchAndSetRoadmapDealUuids();
  }, [fetchAndSetRoadmapDealUuids]);

  const onSaveDealsSelectData = useCallback(() => {
    setErrors(undefined);
    if (roadmapDealUuids.length > MAX_DEALS) {
      setErrors([{ code: "ERROR_15_DEALS_MAXIMUM", message: "Please select no more than 15 deals" }]);
      return;
    }
    const limitedUuids = roadmapDealUuids.slice(0, MAX_DEALS);
    updatePortfolioRoadmapLocalStorage(limitedUuids);
    fetchRoadmapData(limitedUuids);
    setShowAddDealsModal(false);
  }, [fetchRoadmapData, roadmapDealUuids, updatePortfolioRoadmapLocalStorage]);

  useEffect(() => {
    fetchAndSetRoadmapDealUuids();
    fetchSelectData();
  }, [fetchAndSetRoadmapDealUuids, fetchSelectData]);

  return {
    errors,
    ganttRows,
    onRowClick,
    dealsMultiSelectData,
    roadmapDealUuids,
    setRoadmapDealUuids,
    showAddDealsModal,
    setShowAddDealsModal,
    closeSelectDealsModal,
    isLoading,
    hasData,
    onSaveDealsSelectData,
    fetchSelectData,
  };
};
